/**
 * @fileoverview Componente responsável por orquestrar a visualização completa de um ticket.
 */

import React, { useState, useEffect, useContext, useRef, useCallback, lazy, Suspense } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { Paper, makeStyles, CircularProgress, useMediaQuery, useTheme } from "@material-ui/core";

import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";

// ✅ Code Splitting: Lazy load de componentes pesados
import useTriageEnabled from "../../hooks/useTriageEnabled";
import useTicketTriage from "../../hooks/useTicketTriage";
import useTicketSmartApproach from "../../hooks/useTicketSmartApproach";
import SmartApproachBar from "../SmartApproach/SmartApproachBar";
import {
  CONTACT_DRAWER_EXPANDED_KEY,
  CONTACT_DRAWER_EXPANDED_WIDTH,
  CONTACT_DRAWER_MIN_CHAT,
  CONTACT_DRAWER_WIDTH,
  CONTACT_DRAWER_WIDTH_VAR,
  canExpandContactDrawer,
  contactDrawerWidth,
} from "../ContactDrawer/drawerWidth";
const ContactDrawer = lazy(() => import("../ContactDrawer"));
const MessageInput = lazy(() => import("../MessageInputCustom/"));
const MessagesList = lazy(() => import("../MessagesList"));
const MessageSearch = lazy(() => import("../MessageSearch"));

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    containIntrinsicSize: "100% 100%",
    contain: "layout style",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    // Fechado, a conversa passa por baixo do painel (a mesma largura dele).
    marginRight: `calc(-1 * ${contactDrawerWidth})`,
    position: "sticky",
    top: 0,
    alignItems: "stretch",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // Durante o arraste, a margem acompanha a largura do contato sem interpolação.
    '[data-resizing="true"] &': { transition: "none" },
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Painel do contato expandido: só no computador, quando sobra espaço para a
  // conversa. A escolha fica guardada neste navegador.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const rootRef = useRef(null);
  const [roomToExpand, setRoomToExpand] = useState(false);
  const [roomForSideDrawer, setRoomForSideDrawer] = useState(false);
  const [drawerExpanded, setDrawerExpanded] = useState(() => {
    try {
      return localStorage.getItem(CONTACT_DRAWER_EXPANDED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const canExpand = isDesktop && roomToExpand;
  const expanded = canExpand && drawerExpanded;
  // Aba pedida ao abrir o painel do contato (ex.: o selo da triagem abre na aba Triagem).
  const [drawerTab, setDrawerTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  const triageEnabled = useTriageEnabled();
  // Fora de grupos e do LiveChat (a IA da triagem conversa pelo WhatsApp).
  const triageAllowed = triageEnabled && !ticket?.isGroup && ticket?.whatsapp?.provider !== "livechat";
  const triage = useTicketTriage(ticket?.id, triageAllowed);
  // Abordagem inteligente: só nas conversas da API oficial (fora de grupos).
  const smartApproach = useTicketSmartApproach(ticket?.id, ticket?.whatsapp?.provider === "meta-api" && !ticket?.isGroup);

  // O painel relê o ticket depois de pausar/retomar/concluir. Se a resposta
  // chegar depois de trocar de conversa, não pode trocar o ticket da tela.
  const updateTicketIfCurrent = useCallback((next) => {
    setTicket((prev) => (prev?.id && next?.id === prev.id ? next : prev));
  }, []);

  // Ref para manter o ticketId atual acessível dentro do handler do socket
  // Resolve o problema de closure stale quando ticket é removido das dependências
  const ticketIdRef = useRef(ticketId);
  // Id numérico do ticket aberto (a rota traz o uuid; o aviso de exclusão, o id).
  const openTicketIdRef = useRef(null);
  useEffect(() => {
    openTicketIdRef.current = ticket?.id ?? null;
  }, [ticket?.id]);

  useEffect(() => {
    setLoading(true);
    const fetchTicket = async () => {
      try {
        // Quem vê a conversa é o servidor quem diz (nível da empresa).
        const { data } = await api.get("/tickets/" + ticketId);

        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
        // Conversa que o usuário não vê: volta para a lista, com o aviso.
        if (err?.response?.status === 403) history.push("/tickets");
      }
    };
    fetchTicket();
  }, [ticketId, history]);

  // Mantém o ref atualizado quando ticketId muda
  useEffect(() => {
    ticketIdRef.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleReady = () => {
      socket.emit("joinChatBox", `${ticketIdRef.current}`);
    };

    // Também emite joinChatBox imediatamente caso o socket já esteja conectado
    if (socket.connected) {
      socket.emit("joinChatBox", `${ticketIdRef.current}`);
    }

    // Atualização do ticket aberto: relido pela API (o aviso às vezes vem
    // incompleto ou com o dono antigo), no máximo uma vez a cada meio segundo.
    let alive = true;
    let refetchTimer = null;
    const refetchTicket = () => {
      clearTimeout(refetchTimer);
      refetchTimer = setTimeout(async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketIdRef.current);
          // Ainda é a mesma conversa (a pessoa pode ter trocado de ticket).
          if (!alive || data?.id !== openTicketIdRef.current) return;
          setTicket(data);
          if (data.contact) setContact(data.contact);
        } catch (err) {
          // Sem acesso ou excluído: os outros avisos cuidam da tela.
        }
      }, 500);
    };

    const handleTicketUpdate = (data) => {
      // Compara com o id do ticket carregado: a rota traz o uuid (o número tirado
      // dele com parseInt apontava para outro ticket).
      if (data.action === "update" && openTicketIdRef.current && Number(data.ticket?.id) === openTicketIdRef.current) {
        refetchTicket();
      }

      // "delete" também avisa que o ticket saiu de uma aba (mudou de status ou de
      // dono); só a exclusão vem com `deleted` e fecha a conversa.
      if (
        data.action === "delete" &&
        data.deleted === true &&
        openTicketIdRef.current &&
        Number(data.ticketId) === openTicketIdRef.current
      ) {
        history.push("/tickets");
      }
    };

    const handleContactUpdate = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    const handleWhatsAppSession = (data) => {
      if (data.action === "update" && data.session) {
        // Atualiza o whatsapp no ticket se for o mesmo whatsappId
        setTicket((prevTicket) => {
          if (prevTicket.whatsappId === data.session.id) {
            return {
              ...prevTicket,
              whatsapp: {
                ...prevTicket.whatsapp,
                status: data.session.status,
                name: data.session.name
              }
            };
          }
          return prevTicket;
        });
      }
    };

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-ticket`, handleTicketUpdate);
    socket.on(`company-${companyId}-contact`, handleContactUpdate);
    socket.on(`company-${companyId}-whatsappSession`, handleWhatsAppSession);

    return () => {
      alive = false;
      clearTimeout(refetchTimer);
      socket.off("ready", handleReady);
      socket.off(`company-${companyId}-ticket`, handleTicketUpdate);
      socket.off(`company-${companyId}-contact`, handleContactUpdate);
      socket.off(`company-${companyId}-whatsappSession`, handleWhatsAppSession);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, history, socketManager]); // ✅ Removido 'ticket' das dependências para evitar re-renders

  // Espaço de verdade do contêiner (lista de tickets, menu e tela mudam):
  // expandir só aparece quando o painel ganharia largura de fato.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setRoomToExpand(canExpandContactDrawer(entry.contentRect.width));
      setRoomForSideDrawer(entry.contentRect.width >= CONTACT_DRAWER_WIDTH + CONTACT_DRAWER_MIN_CHAT);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CONTACT_DRAWER_EXPANDED_KEY, drawerExpanded ? "1" : "0");
    } catch {
      // Sem armazenamento (aba anônima, bloqueio): vale só enquanto a tela estiver aberta.
    }
  }, [drawerExpanded]);

  const handleToggleDrawerExpanded = useCallback(() => {
    setDrawerExpanded((current) => !current);
  }, []);

  // O cabeçalho da conversa (nome e número do ticket) abre e fecha o painel.
  const handleDrawerToggle = () => {
    setDrawerOpen((current) => !current);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleOpenTriage = () => {
    setDrawerTab("triage");
    setDrawerOpen(true);
  };

  const handleOpenSearch = () => {
    setSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
  };

  const handleMessageSelect = (message) => {
    // Implementa a navegação para a mensagem específica
    // A mensagem será destacada e o scroll será feito até ela
    const messageElement = document.getElementById(`message-${message.id}`);
    // ✅ Verificar se elemento existe E se ainda está no DOM
    if (messageElement && document.contains(messageElement)) {
      try {
        // Scroll suave para a mensagem
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        // Adiciona um destaque temporário à mensagem
        setTimeout(() => {
          // ✅ Verificar novamente antes de adicionar classe
          if (messageElement && document.contains(messageElement)) {
            messageElement.classList.add('message-highlight');
            setTimeout(() => {
              // ✅ Verificar novamente antes de remover classe
              if (messageElement && document.contains(messageElement)) {
                messageElement.classList.remove('message-highlight');
              }
            }, 2000);
          }
        }, 100);
      } catch (error) {
        // ✅ Silenciar erros de DOM (elemento pode ter sido removido)
        console.debug("handleMessageSelect scrollIntoView failed (element removed):", error);
      }
    }
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined || ticket.aiAgent) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerToggle}
          onTicketUpdate={setTicket}
        />
      );
    }
  };

  const renderMessagesList = () => {
    return (
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}><CircularProgress /></div>}>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
        ></MessagesList>
        {smartApproach.run && (
          <SmartApproachBar run={smartApproach.run} onReload={smartApproach.reload} onApplyRun={smartApproach.applyRun} />
        )}
        <MessageInput ticketId={ticket.id} ticketStatus={ticket.status} ticket={ticket} smartApproach={smartApproach} />
      </Suspense>
    );
  };

  return (
    <div
      ref={rootRef}
      className={classes.root}
      id="drawer-container"
      style={{ [CONTACT_DRAWER_WIDTH_VAR]: expanded ? CONTACT_DRAWER_EXPANDED_WIDTH : `${CONTACT_DRAWER_WIDTH}px` }}
    >
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          // Sem espaço para os dois painéis, os dados do contato abrem por
          // cima da conversa; a área de mensagens mantém sua largura.
          [classes.mainWrapperShift]: drawerOpen && roomForSideDrawer,
        })}
      >
        <TicketHeader loading={loading}>
          {renderTicketInfo()}
          <TicketActionButtons
            ticket={ticket}
            onOpenSearch={handleOpenSearch}
            onTicketUpdate={setTicket}
            triageSession={triage.active}
            onOpenTriage={handleOpenTriage}
          />
        </TicketHeader>
        {/* Tags movidas para o menu da setinha na lista de tickets */}
        {/* <Paper>
          <TagsContainer ticket={ticket} />
        </Paper> */}
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <Suspense fallback={null}>
        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          contact={contact}
          loading={loading}
          ticket={ticket}
          triageEnabled={triageAllowed}
          triage={triage}
          requestedTab={drawerTab}
          onRequestedTabShown={() => setDrawerTab(null)}
          onTicketUpdate={updateTicketIfCurrent}
          expanded={expanded}
          onToggleExpanded={canExpand ? handleToggleDrawerExpanded : undefined}
        />
      </Suspense>

      <Suspense fallback={null}>
        <MessageSearch
          open={searchOpen}
          onClose={handleCloseSearch}
          ticketId={ticket.id}
          contactName={contact.name}
          onMessageSelect={handleMessageSelect}
        />
      </Suspense>
    </div>
  );
};

export default Ticket;
